import { useTranslation } from "react-i18next"
import { useFormContext } from "react-hook-form"

import { PlaceOrderFormData, ContactInformationProps } from "types"

const ContactInformation = ({ register }: ContactInformationProps) => {
  const {
    formState: { errors },
    watch,
  } = useFormContext<PlaceOrderFormData>()
  const { t } = useTranslation()

  return (
    <div className="p-4 mb-6 bg-white rounded-lg border shadow-sm border-progress_bar_background text-default_card_text">
      <h2 className="mb-4 text-xl font-bold text-black">
        {t("details.contact_info")}
      </h2>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            {t("contact.first_name", "First Name")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="p-2 w-full rounded-md border border-input_border"
            aria-label={`${t("validation.required", "Required")}: ${t("contact.first_name", "First Name")}`}
            {...register("contactInfo.firstName", {
              required: t("validation.field_required", "This field is required"),
              maxLength: {
                value: 128,
                message: t("validation.input_max_length", "Input cannot exceed 128 characters"),
              },
            })}
          />
          {errors.contactInfo?.firstName && (
            <p className="mt-1 text-sm text-red-500">
              {errors.contactInfo.firstName.message}
            </p>
          )}
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            {t("contact.last_name", "Last Name")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="p-2 w-full rounded-md border border-input_border"
            aria-label={`${t("validation.required", "Required")}: ${t("contact.last_name", "Last Name")}`}
            {...register("contactInfo.lastName", {
              required: t("validation.field_required", "This field is required"),
              maxLength: {
                value: 128,
                message: t("validation.input_max_length", "Input cannot exceed 128 characters"),
              },
            })}
          />
          {errors.contactInfo?.lastName && (
            <p className="mt-1 text-sm text-red-500">
              {errors.contactInfo.lastName.message}
            </p>
          )}
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            {t("contact.email", "Email")} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            className="p-2 w-full rounded-md border border-input_border"
            aria-label={`${t("validation.required", "Required")}: ${t("contact.email", "Email")}`}
            {...register("contactInfo.email", {
              required: t("validation.field_required", "This field is required"),
              maxLength: {
                value: 1023,
                message: t("validation.email_max_length", "Email cannot exceed 1023 characters"),
              },
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t("validation.email_invalid", "Invalid email address"),
              },
            })}
          />
          {errors.contactInfo?.email && (
            <p className="mt-1 text-sm text-red-500">
              {errors.contactInfo.email.message}
            </p>
          )}
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            {t("contact.confirm_email", "Confirm Email")} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            className="p-2 w-full rounded-md border border-input_border"
            aria-label={`${t("validation.required", "Required")}: ${t("contact.confirm_email", "Confirm Email")}`}
            {...register("contactInfo.confirmEmail", {
              required: t("validation.field_required", "This field is required"),
              maxLength: {
                value: 1023,
                message: t("validation.email_max_length", "Email cannot exceed 1023 characters"),
              },
              validate: (value) =>
                value === watch("contactInfo.email") || t("validation.emails_must_match", "Emails must match"),
            })}
          />
          {errors.contactInfo?.confirmEmail && (
            <p className="mt-1 text-sm text-red-500">
              {errors.contactInfo.confirmEmail.message}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export { ContactInformation }
